import { Location } from '@angular/common';
import { booleanAttribute, Component, inject, input, signal } from '@angular/core';
import { Router } from '@angular/router';

import { IconComponent } from '@reforgium/icon';
import { LangPipe } from '@reforgium/presentia';
import { SkButtonComponent, SkConfirmDialogComponent, SkDynamicDialogService } from '@ss/ui';

import { urls } from 'constants/urls.constant';

import { DocumentService } from 'use-cases/document.service';
import { UploadDialog } from 'features/dialogs/upload-dialog';

@Component({
  selector: 'app-document-actions',
  template: `
    <section class="flex items-center justify-between gap-4 flex-wrap">
      <button type="button" class="flex items-center gap-2 h-[24px] cursor-pointer" (click)="handleBack()">
        <re-icon class="rotate-180" name="chevron" width="20" height="24" />
        {{ title() | lang }}
      </button>

      <div class="flex gap-2 flex-wrap justify-end">
        @if (uploadable()) {
          <sk-button
            appearance="success"
            icon="upload"
            [label]="'administrative.upload.action' | lang"
            (clicked)="handleUpload()"
          />
        }
        @if (addable()) {
          <sk-button icon="plus" [label]="'administrative.buttons.addRevision' | lang" (clicked)="handleAdd()" />
        }
        @if (editable() && documentId()) {
          <sk-button
            appearance="secondary"
            icon="edit"
            [label]="'administrative.buttons.edit' | lang"
            (clicked)="handleEdit()"
          />
        }

        @if (deletable() && documentId()) {
          <sk-button
            appearance="danger"
            icon="trash"
            [label]="'administrative.buttons.deleteRevision' | lang"
            (clicked)="handleDelete()"
          />
        }
      </div>
    </section>

    @if (deletable() && documentId()) {
      <sk-confirm-dialog
        [(visible)]="deleteConfirmVisible"
        [header]="'administrative.deleteConfirmation.title' | lang"
        [message]="'administrative.deleteConfirmation.message' | lang"
        [acceptLabel]="'administrative.deleteConfirmation.confirm' | lang"
        [rejectLabel]="'administrative.deleteConfirmation.cancel' | lang"
        (accept)="deleteDocument()"
      />
    }
  `,
  imports: [IconComponent, LangPipe, SkButtonComponent, SkConfirmDialogComponent],
  styles: `
    @media screen and (max-width: 768px) {
      .action-label {
        display: none;
      }
    }
  `,
})
export class DocumentActions {
  title = input.required<string>();
  versionId = input<number | undefined>();
  documentId = input<number | undefined>();
  uploadable = input(false, { transform: booleanAttribute });
  editable = input(false, { transform: booleanAttribute });
  deletable = input(false, { transform: booleanAttribute });
  addable = input(false, { transform: booleanAttribute });
  dialog = inject(SkDynamicDialogService);
  router = inject(Router);
  location = inject(Location);
  documentService = inject(DocumentService);
  deleteConfirmVisible = signal(false);

  handleBack(): void {
    if (this.router.lastSuccessfulNavigation()?.previousNavigation) {
      this.location.back();

      return;
    }

    const categoryId = this.router.parseUrl(this.router.url).queryParams['categoryId'];

    void this.router.navigate([urls.documentsList], {
      queryParams: categoryId ? { categoryId } : undefined,
    });
  }

  handleAdd() {
    void this.router.navigate([urls.documentCreation], {
      queryParams: {
        documentId: this.documentId(),
      },
    });
  }

  handleEdit = () =>
    this.router.navigate([urls.documentEdit(this.documentId())], {
      queryParams: {
        versionId: this.versionId(),
      },
    });

  handleDelete = () => this.deleteConfirmVisible.set(true);

  deleteDocument() {
    const versionId = this.versionId();

    if (versionId) {
      this.documentService.documentVersion
        .delete({ params: { versionId } })
        .then(() => this.router.navigate([urls.documentsList]));
    }
  }

  handleUpload = () => {
    const versionId = this.versionId();

    if (versionId) {
      this.dialog.open(UploadDialog, {
        inputs: { versionId },
      });
    }
  };
}
